import { useState, useEffect } from 'react';

// Define the interface for side panel state
export interface SidePanelState {
  isOpen: boolean;
  position: 'left' | 'right';
  width: number;
  activeTab: 'users' | 'chat' | 'notes' | 'tasks';
  selectedNodeId?: string | null;
  pinnedNodeId?: string | null;
}

// Default state for the side panel
const defaultState: SidePanelState = {
  isOpen: false,
  position: 'left',
  width: 500, // Increased from 510 to 600
  activeTab: 'users',
  selectedNodeId: null,
  pinnedNodeId: null
};

/**
 * Hook to manage the side panel state with persistence
 */
export function useSidePanelState() {
  // Initialize state from localStorage or use default values
  const [state, setState] = useState<SidePanelState>(() => {
    if (typeof window === 'undefined') return defaultState;
    
    try {
      const saved = localStorage.getItem('presence-side-panel-state');
      // Always initialize with closed panel state, regardless of saved state
      return saved ? { ...JSON.parse(saved), isOpen: false } : defaultState;
    } catch (error) {
      console.error('Error loading side panel state:', error);
      return defaultState;
    }
  });
  
  // Save state changes to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('presence-side-panel-state', JSON.stringify(state));
    }
  }, [state]);
  
  // Functions to update state
  const togglePanel = () => {
    setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  };
  
  const setPosition = (position: 'left' | 'right') => {
    setState(prev => ({ ...prev, position }));
  };
  
  const setWidth = (width: number) => {
    setState(prev => ({ ...prev, width }));
  };
  
  const setActiveTab = (activeTab: 'users' | 'chat' | 'notes' | 'tasks') => {
    setState(prev => ({ ...prev, activeTab }));
  };
  
  const closePanel = () => {
    setState(prev => ({ ...prev, isOpen: false }));
  };
  
  const openPanel = () => {
    setState(prev => ({ ...prev, isOpen: true }));
  };
  
  // Function to select a node to display in the panel
  const selectNode = (nodeId: string | null) => {
    setState(prev => {
      // If a node is pinned, don't change selection
      if (prev.pinnedNodeId) {
        return prev;
      }
      
      return { 
        ...prev, 
        selectedNodeId: nodeId,
        isOpen: nodeId ? true : prev.isOpen // Open panel when a node is selected
      };
    });
  };
  
  // Function to pin/unpin a node
  const pinNode = (nodeId: string | null) => {
    setState(prev => {
      // If we're clicking the same pinned node, unpin it
      if (nodeId === prev.pinnedNodeId) {
        return {
          ...prev,
          pinnedNodeId: null
        };
      } else {
        // Pin the new node and make sure it's selected and panel is open
        return {
          ...prev,
          pinnedNodeId: nodeId,
          selectedNodeId: nodeId,
          isOpen: nodeId ? true : prev.isOpen
        };
      }
    });
  };
  
  // Check if a node is pinned
  const isPinned = (nodeId: string | null) => {
    return nodeId !== null && state.pinnedNodeId === nodeId;
  };
  
  // The actual selected node ID might be either the selected or pinned one
  const actualSelectedNodeId = state.pinnedNodeId || state.selectedNodeId;
  
  return {
    state: {
      ...state,
      selectedNodeId: actualSelectedNodeId
    },
    togglePanel,
    setPosition,
    setWidth,
    setActiveTab,
    closePanel,
    openPanel,
    selectNode,
    pinNode,
    isPinned
  };
}

export default useSidePanelState;